import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../../components/Header";
import { Button, Icon, Input, message, Row, Spin, Table, Tooltip } from "antd";
import Style from "./index.module.scss";
import Nav from "../../components/Nav";
import {
    deleteSource,
    getSourceList,
    NavItemProps,
    SHOW_MODAL,
    SourceItem,
    SourceProps
} from "../../service";
import useRequest from "../../../../utils/useRequest";
import usePagination from "../../../../utils/v2/usePagination";
import useSelection from "../../../../utils/v2/useSelection";
import moment from "moment";
import { ColumnProps } from "antd/lib/table/interface";
import useRequestFn from "../../../../utils/useRequestFn";
import { ResultMessage } from "../../../../components/ResultMessage";
import { errorMessage } from "../../../../service/service";
import {
    getChapter,
    getChapterProp
} from "../../../../service/CourseDetail/chapter";
import TreeMain from "./Components/TreeMain";
import DeleteConfirm from "../../../../components/DeleteConfirm";
import className from "classnames";
import lodash from "lodash-es";
import { overFlow } from "../../../../components/overFlow";
import PreviewSourceModal from "../../../../components/PreviewSourceModal";
import FileItem from "../../../../components/FileItem";
import useModalHandler from "../../../../utils/v2/useModalHandler";
import RecycleBinModal from "../../../RecycleBin";

const defaultQuery = {
    batchNo: "",
    courseNo: "",
    courseSeq: "",
    delStatus: "2"
};

const { Search } = Input;

interface Prop {
    show: "window" | "page";
    onCheck?: (checks: number[]) => void;
}

const ResourcePool = ({ show = "page", onCheck }: Prop) => {
    const [MODEL, TOGGLE_MODEL] = useState<SHOW_MODAL>(SHOW_MODAL.TABLE);
    const rowSelection = useSelection();

    function toggle() {
        if (MODEL === SHOW_MODAL.TABLE) {
            TOGGLE_MODEL(SHOW_MODAL.TREE);
        } else {
            TOGGLE_MODEL(SHOW_MODAL.TABLE);
        }
    }

    // table start
    const pagination = usePagination(9999);
    const [query, setQuery] = useState<SourceProps>(defaultQuery);
    const [keyword, setKeyword] = useState("");
    const [deleteAction, { loading: deleteLoading }] =
        useRequestFn(deleteSource);

    const getParams = useMemo(() => {
        return {
            sourceFileEntity: {
                useType: "1"
            },
            ...query,
            ...pagination.params,
            keyword
        };
    }, [query, keyword, pagination.params, show]);
    const [data, { loading: tableLoading }, refreshTable] = useRequest(
        () => getSourceList(getParams),
        {},
        [query, pagination.params, keyword]
    );
    function onNavSelect(e: NavItemProps) {
        rowSelection.setSelected([]);
        setQuery({
            delStatus: "2",
            batchNo: e.batchNo,
            courseNo: e.courseno,
            courseSeq: e.courseseq
        });
    }

    async function onDelete() {
        try {
            const params = rowSelection.selected
                .map(item => {
                    return {
                        deleteFromSource: "1",
                        sourceId: item,
                        type: "1"
                    };
                })
                .filter(item => {
                    return !item.sourceId.toString().includes("NODE");
                });
            if (params.length > 0) {
                await deleteAction(params);
                await Promise.all([refreshTable(), refreshTree()]);
                message.success("删除成功！");
            }
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    //tree start

    const nodeParams: getChapterProp = {
        dcCoursewareSourceParam: {
            delStatus: "2",
            sourceFileEntity: {
                useType: "1"
            }
        },
        dcCourseChapterEntity: {
            batchNo: query.batchNo,
            delStatus: "2",
            courseNo: query.courseNo,
            courseSeq: query.courseSeq
        }
    };
    const [treeData, { loading: treeLoading }, refreshTree] = useRequest(
        () => getChapter(nodeParams),
        {},
        [query]
    );

    function isPage(children: React.ReactChild) {
        if (show === "page") {
            return children;
        }
        return;
    }

    const yHeight = useMemo(() => {
        switch (show) {
            case "page":
                return "calc(100vh - 294px)";
            case "window":
                return "calc(100vh - 354px)";
            default:
                return "100%";
        }
    }, [show]);

    useEffect(() => {
        rowSelection.setSelected([]);
    }, [MODEL]);

    function isFile(v: any): v is SourceItem {
        if (!v) {
            return false;
        }
        return "sourceFileEntity" in v;
    }

    useEffect(() => {
        const selected = rowSelection.selected.filter(item => {
            if (typeof item === "string") {
                return !item?.includes("NODE_");
            } else {
                return false;
            }
        });
        const params: number[] = [];
        selected.forEach(id => {
            if (data) {
                const obj = lodash.find(data.data, { id });
                if (isFile(obj)) {
                    params.push(obj.sourceFileEntity.id);
                }
            }
        });
        onCheck?.(params);
    }, [rowSelection.selected]);

    //预览
    const prevModal = useModalHandler(PreviewSourceModal);
    function onPrev(id: number) {
        prevModal.setPayload(id);
        prevModal.open();
    }

    const columns: ColumnProps<SourceItem>[] = [
        {
            title: "名称",
            dataIndex: "sourceFileEntity.fileName",
            width: 220,
            render: (text: string, item) => {
                return (
                    <FileItem
                        nameClick={() => onPrev(item.id)}
                        text={
                            <Tooltip placement="topLeft" title={text}>
                                {text}
                            </Tooltip>
                        }
                        fileType={item?.sourceFileEntity?.fileType}
                    />
                );
            }
        },
        {
            title: "所属学期",
            dataIndex: "batchName",
            render: (text: string, item) => overFlow(text)
        },
        {
            title: "所属课程",
            render: (text, item) =>
                overFlow(
                    `[${item.courseNo}-${item.courseSeq}]${item.courseName}`
                )
        },
        {
            title: "所属章节",
            dataIndex: "ownerChapter.chapterName",
            render: (text: string, item) =>
                overFlow(
                    item.ownerChapter
                        ? item.ownerChapter.chapterName
                        : "章节目录"
                )
        },
        {
            title: "课件说明",
            dataIndex: "description",
            render: (text: string, item) => overFlow(text)
        },
        {
            title: "课件大小",
            dataIndex: "sourceFileEntity.fileSize",
            render: (text: string, item) => overFlow(text)
        },
        {
            title: "上传时间",
            dataIndex: "createTime",
            render: (text: string, item) =>
                overFlow(moment(text).format("YYYY-MM-DD HH:mm:ss"))
        }
    ];

    const recycleBinModal = useModalHandler(RecycleBinModal, { remove: true });

    return (
        <div>
            {recycleBinModal.render(<RecycleBinModal width={1000} />)}
            {isPage(<Header style={{ marginBottom: 0 }} navIsShow={false} />)}
            <Row
                type="flex"
                className={className(
                    Style.main,
                    show === "page" ? Style.page : Style.window
                )}>
                <div style={{ width: 290 }}>
                    <Nav onSelect={onNavSelect} />
                </div>
                <div className={Style.right}>
                    <Spin spinning={deleteLoading}>
                        <div className={Style.tools}>
                            <div className={Style.btnrow}>
                                {show === "page" && (
                                    <div
                                        onClick={recycleBinModal.open}
                                        className={Style.recy}>
                                        <Icon type="delete" />
                                        回收站
                                    </div>
                                )}
                                <div
                                    className={Style.toggleBtn}
                                    onClick={toggle}>
                                    <Icon type="unordered-list" />
                                    切换至
                                    {MODEL === SHOW_MODAL.TREE
                                        ? "课程列表"
                                        : "课程章节"}
                                    模式
                                </div>
                            </div>
                            {MODEL === SHOW_MODAL.TABLE && (
                                <div className={Style.Right}>
                                    <Search
                                        className="BORDER-RADIUS-2"
                                        placeholder="输入关键词搜索"
                                        onSearch={value => setKeyword(value)}
                                        enterButton
                                    />
                                    {isPage(
                                        <DeleteConfirm
                                            title="确定删除吗？"
                                            onSuccess={onDelete}>
                                            <Button
                                                className="BORDER-RADIUS-2"
                                                style={{ marginLeft: 15 }}
                                                disabled={
                                                    rowSelection.selected
                                                        .length === 0
                                                }
                                                type="danger">
                                                删除
                                            </Button>
                                        </DeleteConfirm>
                                    )}
                                </div>
                            )}
                        </div>
                    </Spin>
                    <div className={Style.content}>
                        {MODEL === SHOW_MODAL.TABLE && (
                            <Table
                                loading={tableLoading}
                                rowKey="id"
                                rowSelection={{
                                    selectedRowKeys:
                                        rowSelection.selected as number[],
                                    onChange: rowSelection.setSelected
                                }}
                                className={Style.table}
                                pagination={false}
                                dataSource={data?.data}
                                columns={columns}
                                scroll={{ y: yHeight }}
                            />
                        )}
                        {MODEL === SHOW_MODAL.TREE && (
                            <Spin spinning={treeLoading}>
                                <TreeMain
                                    show={show}
                                    rowSelection={rowSelection}
                                    data={treeData || []}
                                />
                            </Spin>
                        )}
                    </div>
                </div>
            </Row>

            {prevModal.render(<PreviewSourceModal data={data?.data || []} />)}
        </div>
    );
};

export default ResourcePool;
